/**
 * A store's keyword override, which has three states rather than two.
 *
 * `store_settings.default_keywords` is nullable on purpose, so there are three
 * answers and not two:
 *
 *   null  - inherit. Generation uses the clinic's list.
 *   []    - this store deliberately carries no keywords, not even the clinic's.
 *   [...] - this store's own list, replacing the clinic's.
 *
 * A single tag field can only express two of those - clearing the tags would
 * have to mean either "inherit" or "none", and whichever it meant, the other
 * state would be unreachable from the screen while remaining reachable through
 * the API. So the checkbox is not decoration: it is the third state, made
 * visible.
 *
 * Controlled on `string[] | null`, which is exactly what the PATCH body takes,
 * so no caller has to reassemble it.
 */
import { KeywordInput } from './KeywordInput.tsx';
import { Pill } from './ui.tsx';

export function KeywordOverride({
  id,
  value,
  inherited,
  onChange,
  disabled,
}: {
  id: string;
  /** null = inherit from the clinic. */
  value: string[] | null;
  /** The clinic's list, shown while inheriting and used to seed an override. */
  inherited: string[];
  onChange: (next: string[] | null) => void;
  disabled?: boolean;
}) {
  return (
    <>
      <label className="checkline">
        <input
          type="checkbox"
          checked={value !== null}
          disabled={disabled}
          /*
           * Turning the override on starts from the clinic's list rather than
           * from nothing: "the clinic's, plus ours" is the common case, and
           * retyping the shared keywords to get there is work the screen can do.
           * Turning it off restores inheritance rather than emptying the list.
           */
          onChange={(event) => onChange(event.target.checked ? [...inherited] : null)}
        />
        <span>この店舗で独自のキーワードを設定する</span>
      </label>
      {value === null ? (
        <span className="field__hint">
          クリニックの既定を使用します：{inherited.join('・') || '（未設定）'}
        </span>
      ) : (
        <>
          <KeywordInput id={id} value={value} onChange={onChange} disabled={disabled ?? false} />
          {value.length === 0 && (
            <span className="field__hint">
              空のままにすると、この店舗の記事にはキーワードを渡しません。
            </span>
          )}
        </>
      )}
    </>
  );
}

/**
 * The same three states, read-only.
 *
 * Kept beside the editor because they have to agree: a detail panel that shows
 * an inherited list without saying it is inherited is how somebody edits the
 * clinic's keywords believing they are editing one store's.
 */
export function InheritedKeywords({
  own,
  inherited,
}: {
  own: string[] | null;
  inherited: string[];
}) {
  if (own === null) {
    if (inherited.length === 0) return <span className="muted">未設定</span>;
    return (
      <span className="muted">
        {inherited.join('・')} <Pill tone="muted">クリニックから継承</Pill>
      </span>
    );
  }
  if (own.length === 0) {
    return (
      <>
        <Pill tone="muted">この店舗はキーワードなし</Pill>{' '}
        <span className="muted small">クリニックの既定も使いません</span>
      </>
    );
  }
  return <>{own.join('・')}</>;
}
